/**
 * TexchemeLogo
 * -----------------------------------------------------------------------------
 * Renders the official Texcheme lockup (Phoenix-in-Oval + TEXCHEME wordmark).
 * Two files must exist under /public/ (they are ~30 KB PNGs at 1757×330):
 *   - texcheme-logo.png       — Primary (Full Color)   • red icon + slate wordmark
 *   - texcheme-logo-white.png — Reversed (All White)   • both parts white
 *
 * These are the two approved configurations from Brand Guideline V1, page 26.
 *
 * Props:
 *   variant : "primary" (light bg) | "reverse" (dark bg) | "mono" (single-colour)
 *   size    : approx icon height in px (wordmark scales with it)
 *   showWordmark : reserved (icon-only crop not yet extracted; ignored today).
 *   showTagline  : renders "Your Chemicals. Our Delivery." caption to the right.
 */
import { useMemo } from "react";

const FILES = {
  primary: "/texcheme-logo.png",
  reverse: "/texcheme-logo-white.png",
  mono:    "/texcheme-logo.png", // TODO: extract all-slate variant if needed
};

// Natural aspect ratios of the exported PNGs
// primary: 1757 × 330 ≈ 5.32:1
// reverse: 1626 × 314 ≈ 5.18:1 (background removed, tight crop)
const LOGO_AR_MAP = {
  primary: 1757 / 330,
  reverse: 1626 / 314,
  mono:    1757 / 330,
};

export default function TexchemeLogo({
  variant = "primary",
  size = 32,
  showWordmark = true,
  showTagline = false,
  wordmarkColor = null,   // when set (e.g. "#fff"), renders wordmark as inline text instead of using PNG lockup
  className = "",
}) {
  const LOGO_AR = LOGO_AR_MAP[variant] || LOGO_AR_MAP.primary;
  // "size" is the intended icon height; the horizontal lockup is ~5.3× wider.
  // We render at a proportional pixel height so the lockup lines up cleanly.
  const height = useMemo(() => Math.round(size * 1.1), [size]);
  const width = Math.round(height * LOGO_AR);
  const src = FILES[variant] || FILES.primary;

  // Split mode — clip the PNG to the icon-only leftmost square and render
  // "TEXCHEME" as HTML text in the caller-supplied colour.
  if (wordmarkColor) {
    // Icon occupies roughly the leftmost ~19% of the 1757×330 PNG.
    const iconBoxWidth = height; // icon is ~square
    return (
      <span
        className={`tc-logo tc-logo-split ${className}`}
        data-testid="texcheme-logo"
        style={{ display: "inline-flex", alignItems: "center", gap: 10 }}
      >
        <span
          style={{
            display: "inline-block",
            width: iconBoxWidth,
            height,
            overflow: "hidden",
            flex: "none",
          }}
        >
          <img
            src={FILES.primary}
            alt=""
            aria-hidden="true"
            style={{
              height,
              width: Math.round(height * LOGO_AR),
              display: "block",
              objectFit: "cover",
              objectPosition: "left center",
            }}
          />
        </span>
        <span
          style={{
            fontFamily: "Sora, sans-serif",
            fontWeight: 700,
            letterSpacing: ".12em",
            fontSize: Math.round(size * 0.62),
            color: wordmarkColor,
            lineHeight: 1,
            textTransform: "uppercase",
          }}
        >
          Texcheme
        </span>
      </span>
    );
  }

  const tagline = showTagline ? (
    <span
      className="tc-tagline"
      style={{
        marginLeft: 4,
        paddingLeft: 12,
        borderLeft: `1px solid ${
          variant === "reverse" ? "rgba(255,255,255,.24)" : "rgba(74,92,110,.24)"
        }`,
        fontSize: 10.5,
        letterSpacing: ".18em",
        textTransform: "uppercase",
        fontWeight: 500,
        color: variant === "reverse" ? "rgba(255,255,255,.72)" : "var(--tc-slate)",
        lineHeight: 1.15,
      }}
    >
      Your Chemicals.<br />Our Delivery.
    </span>
  ) : null;

  return (
    <span
      className={`tc-logo ${className}`}
      data-testid="texcheme-logo"
      style={{ display: "inline-flex", alignItems: "center", gap: 12 }}
    >
      <img
        src={src}
        alt="Texcheme"
        loading="eager"
        decoding="async"
        style={{ height, width: showWordmark ? width : "auto", display: "block" }}
      />
      {tagline}
    </span>
  );
}
